import Link from 'next/link';
import type { Palette, ServiceDetail } from './types';

type ServicesProps = { palette: Palette; serviceDetails: ServiceDetail[] };

export function ServicesSection({ palette, serviceDetails }: ServicesProps) {
  return (
    <section
      id="services"
      className="relative overflow-hidden px-6 py-12 scroll-mt-24 md:scroll-mt-28 lg:px-8"
      style={{ backgroundColor: '#F9FAFB' }}
    >
      {/* --- BACKGROUND ACCENTS (Kept for pop) --- */}
      <div 
        className="absolute left-0 top-20 h-[500px] w-[500px] -translate-x-1/3 rounded-full opacity-30 blur-[100px]"
        style={{ background: palette.sage }}
      />
      <div 
        className="absolute right-0 bottom-0 h-[400px] w-[400px] translate-x-1/3 rounded-full opacity-20 blur-[80px]"
        style={{ background: palette.clay }}
      />

      <div className="relative mx-auto max-w-7xl">
        
        {/* COMPACT HEADER: Title Left / Action Right */}
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between mb-10">
          
          {/* Left Side: Title & Text */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 backdrop-blur-sm">
               <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: palette.clay }} />
               Simplified Solutions
            </div>
            
            <h2 className="mt-3 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              The big upgrades, <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-slate-900">
                minus the big headache.
              </span>
            </h2>
            
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              We consolidate Roofing, Water, Lighting, and Electrical Upgrades so you stop juggling contractors.
            </p>
          </div>
          
          {/* Right Side: Quote & Button (Now inline with content) */}
          <div className="shrink-0 lg:text-right flex flex-col items-start lg:items-end gap-2">
             <p className="text-xs font-medium italic text-slate-500">
               "Finally, a company that just handles it."
             </p>
             <a
              href="/home-pros#quote"
              className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/20"
              style={{ backgroundColor: palette.clay }}
            >
              Start Your Project
              <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </div>
        </div>

        {/* CARDS GRID */}
        <div className="grid gap-6 lg:grid-cols-2">
          {serviceDetails.map((service) => {
            const isLightAccent = service.accent === '#f6d7ba' || service.accent === '#f1c27d';
            const btnTextColor = isLightAccent ? '#1F2937' : '#FFFFFF';

            return (
              <article
                key={service.title}
                className="group relative flex flex-col justify-between rounded-[24px] border border-white bg-white/80 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                style={{ '--accent': service.accent } as React.CSSProperties}
              >
                {/* Hover Glow */}
                <div 
                  className="absolute inset-0 -z-10 rounded-[24px] opacity-0 transition-opacity duration-500 group-hover:opacity-10"
                  style={{ background: service.accent }}
                />

                {/* Badge */}
                {service.badge && (
                  <span
                    className="absolute right-5 top-5 rounded-full px-2 py-1 text-[0.6rem] font-bold uppercase tracking-[0.15em] shadow-sm"
                    style={{ backgroundColor: palette.clay, color: '#FFF' }}
                  >
                    {service.badge}
                  </span>
                )}

                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                     <div 
                       className="flex h-10 w-10 items-center justify-center rounded-xl shadow-inner"
                       style={{ backgroundColor: `${service.accent}20` }}
                     >
                        <div className="h-3 w-3 rounded-full shadow-sm" style={{ backgroundColor: service.accent }} />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-slate-900">{service.title}</h3>
                       <div className="mt-1 h-0.5 w-0 rounded-full bg-slate-200 transition-all duration-500 group-hover:w-full" style={{ backgroundColor: service.accent }} />
                     </div>
                  </div>
                  
                  <p className="text-sm leading-relaxed text-slate-700">
                    {service.benefit}
                  </p>
                  
                  {/* Compact Bullets */}
                  <ul className="space-y-2 rounded-xl bg-slate-50 p-4">
                    {service.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2.5 text-xs font-medium text-slate-600">
                        <svg className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: service.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Compact Action Area */}
                <div className="mt-6 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                  <a
                    href="#quote"
                    className="flex-1 rounded-lg py-3 text-center text-[10px] font-bold uppercase tracking-[0.2em] shadow-sm transition-all hover:scale-[1.02] hover:shadow-md"
                    style={{ 
                      backgroundColor: service.accent, 
                      color: btnTextColor 
                    }} 
                  >
                    Handle this
                  </a>
                  <Link
                    href={`/home-pros/${service.slug}`}
                    className="group/link px-2 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 transition-colors hover:text-slate-600"
                  >
                    Details
                    <span className="transition-transform group-hover/link:translate-x-1">→</span>
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
